import { loader } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import WorkerEditor from 'monaco-editor/editor/editor.worker.js?worker';
import WorkerJson from 'monaco-editor/language/json/json.worker.js?worker';
import WorkerCss from 'monaco-editor/language/css/css.worker.js?worker';
import WorkerHtml from 'monaco-editor/language/html/html.worker.js?worker';
import WorkerTs from 'monaco-editor/language/typescript/ts.worker.js?worker';
import { temaEfetivo, usarTema } from '../estado/tema';
import { extensaoDe } from '../formato';

/**
 * Monaco servido pelo próprio domínio, nunca por CDN.
 *
 * A CSP do painel não permite script de fora, e nem deveria: o editor abre
 * arquivo de projeto e recebe conteúdo das máquinas. A configuração padrão do
 * pacote carregaria tudo de um CDN, então os workers são declarados aqui e
 * empacotados junto.
 *
 * Os workers vêm pelo sufixo `?worker`, que é como o Vite os empacota. A
 * tentativa anterior — `new Worker(new URL('monaco-editor/…', import.meta.url))`
 * — parecia funcionar e não funcionava: o Vite só reescreve `new URL` quando o
 * caminho é relativo e literal, então um especificador de pacote virava um
 * endereço inexistente. O Monaco engolia a falha ("Could not create web
 * worker(s)"), caía para a thread principal e **parava de calcular diferença**
 * — era por isso que todo diff aparecia sem uma linha vermelha sequer.
 */
self.MonacoEnvironment = {
  getWorker(_id: string, rotulo: string): Worker {
    if (rotulo === 'json') return new WorkerJson();
    if (rotulo === 'css' || rotulo === 'scss' || rotulo === 'less') return new WorkerCss();
    if (rotulo === 'html' || rotulo === 'handlebars' || rotulo === 'razor') return new WorkerHtml();
    if (rotulo === 'typescript' || rotulo === 'javascript') return new WorkerTs();
    // O genérico é o que calcula o diff: sem ele, comparação nenhuma aparece.
    return new WorkerEditor();
  },
};

loader.config({ monaco });

/**
 * O tema do Monaco é global e vale também fora do editor.
 *
 * Sem acertá-lo aqui, o primeiro `colorir()` numa conversa — antes de qualquer
 * editor abrir — pintaria com o tema errado, e o bloco de código sairia escuro
 * sobre escuro. A assinatura acompanha a escolha do painel: trocar o tema
 * repinta o que já está na tela, sem recarregar.
 */
function sincronizarTema(): void {
  monaco.editor.setTheme(temaEfetivo(usarTema.getState()) === 'claro' ? 'vs' : 'vs-dark');
}

sincronizarTema();
usarTema.subscribe(sincronizarTema);

/**
 * Pinta um trecho de código fora do editor.
 *
 * É o tokenizador do Monaco, que já está carregado e servido pelo próprio
 * domínio: nenhuma biblioteca de destaque entra no pacote só para deixar o
 * bloco de código da conversa legível. Devolve HTML pronto — o Monaco escapa o
 * texto ao tokenizar, então o que volta são só `span` com cor.
 *
 * Em qualquer falha devolve string vazia, e quem chamou mostra o texto cru:
 * conversa não pode sumir porque o destaque não deu certo.
 */
export async function colorir(texto: string, linguagem: string): Promise<string> {
  try {
    return await monaco.editor.colorize(texto, linguagem, {});
  } catch {
    return '';
  }
}

/** A linguagem pelo nome que veio na cerca do markdown (```ts, ```bash…). */
export function linguagemDoRotulo(rotulo: string): string {
  const limpo = rotulo.trim().toLowerCase();
  if (!limpo) return 'plaintext';
  const apelidos: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    console: 'shell',
    yml: 'yaml',
    py: 'python',
    ps: 'powershell',
    ps1: 'powershell',
    text: 'plaintext',
    txt: 'plaintext',
  };
  return apelidos[limpo] ?? limpo;
}

/** Linguagem pelo fim do nome; o resto o Monaco resolve sozinho. */
export function linguagemDe(caminho: string): string {
  const extensao = extensaoDe(caminho);
  const mapa: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    mjs: 'javascript',
    cjs: 'javascript',
    json: 'json',
    md: 'markdown',
    css: 'css',
    scss: 'scss',
    html: 'html',
    yml: 'yaml',
    yaml: 'yaml',
    sh: 'shell',
    bash: 'shell',
    ps1: 'powershell',
    py: 'python',
    sql: 'sql',
    prisma: 'graphql',
    toml: 'ini',
    ini: 'ini',
    dockerfile: 'dockerfile',
  };
  return mapa[extensao] ?? 'plaintext';
}
